import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminPageHeader,
  AdminReviewEditorFields,
  appendYowanContentPreviewQuery,
  useAdminContentMediaOptions,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import {
  fetchYowanAdminFounderDetail,
  getYowanAdminMutationErrorMessage,
  updateYowanAdminFounderCassette,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const AdminFounderEditPage = () => {
  const content = yowanPageContent['/admin/content/founder/:cassetteId/edit'];
  const navigate = useNavigate();
  const preservedDomainSearch = usePreservedDomainSearch();
  const { cassetteId } = useParams();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    mediaAssetOptions,
    mediaAssetLoading,
    mediaAssetError,
  } = useAdminContentMediaOptions('founder', currentRecord?.primary_image);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues:
      currentRecord || {
        title: '',
        body: '',
        displayOrder: 1,
        isPublished: false,
        primary_image_id: '',
      },
  });

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setSubmitMessage('');

    fetchYowanAdminFounderDetail(cassetteId)
      .then((record) => {
        if (isMounted) {
          setCurrentRecord(record);
          reset(record);
        }
      })
      .catch(() => {
        if (isMounted) {
          setCurrentRecord(null);
          setSubmitMessage('コンテンツを読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [cassetteId, reset]);

  if (!content) {
    return null;
  }

  if (!currentRecord) {
    return (
      <AdminBlogShell activeNavHref="/admin/content/founder" routeKey="/admin/content/founder/:cassetteId/edit">
        <AdminPageHeader
          title={content.header.title}
          paragraphs={content.header.paragraphs}
          actions={[
            <Link key="back-to-list" to={appendYowanContentPreviewQuery('/admin/content/founder', preservedDomainSearch)}>
              一覧へ戻る
            </Link>,
          ]}
        />
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <p className="yowan-admin-form__message">{submitMessage || 'コンテンツが見つかりませんでした。'}</p>
        )}
      </AdminBlogShell>
    );
  }

  const onSubmit = async (formValues) => {
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const updated = await updateYowanAdminFounderCassette(cassetteId, formValues);
      setSubmitMessage('保存しました。');
      if (updated?.id || cassetteId) {
        navigate(appendYowanContentPreviewQuery('/admin/content/founder', preservedDomainSearch));
      }
    } catch (error) {
      setSubmitMessage(getYowanAdminMutationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref="/admin/content/founder" routeKey="/admin/content/founder/:cassetteId/edit">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanContentPreviewQuery('/admin/content/founder', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />

      <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        <AdminReviewEditorFields
          content={content}
          register={register}
          errors={errors}
          mediaAssetOptions={mediaAssetOptions}
          mediaAssetLoading={mediaAssetLoading}
          mediaAssetError={mediaAssetError}
        />

        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting}>
            {content.form.submitLabel}
          </button>
        </div>

        {isSubmitting ? (
          <p className="yowan-admin-form__message yowan-admin-form__message--pending">
            <LoadingSpinner inline size="sm" />
            <span>{content.form.pendingMessage}</span>
          </p>
        ) : submitMessage ? (
          <p className="yowan-admin-form__message">{submitMessage}</p>
        ) : null}
      </form>
    </AdminBlogShell>
  );
};

export default AdminFounderEditPage;
